import React, { useCallback, useState } from 'react';
import * as input from '../connectedForm/inputComponents';
import { default as Form } from '../connectedForm';
import { maxMakeable } from '../../graphql/entities/product/Product';
import { format } from 'date-fns';
import SalesOrderLineWorkspace from '../../views/sales/SalesOrderLineWorkspace';
import PurchaseOrderLineWorkspace from '../../views/purchases/PurchaseOrderLineWorkspace';
import ProductFormModal from '../ProductFormModal';
import ReactTooltip from 'react-tooltip';
import WorkspaceModal from '../Workspace/WorkspaceModal';
import SalesCreditWorkspace from '../../views/salesLedger/SalesCreditWorkspace';
import { usePrecision } from '../../utils/hooks';
import {
  CostViewPermission,
  FilterOperation,
  ICustomer,
  IFilterInput,
  IItemInformation,
  IProduct,
  IPurchaseOrder,
  ISalesCredit,
  ISalesInvoice,
  ISalesOrder,
  ISupplier,
  ISupplierReturn,
  Maybe,
} from '../../graphql/schema/p4types';
import { useSelector } from 'react-redux';
import { getLineDiscountInformation } from './LineDetailsPanel';
import HintIcon from '../connectedForm/HintIcon';
import { useSymbolFromCurrency } from '../../utils/CurrencyUtil';
import { BlurTextFieldType } from '../connectedForm/BlurTextField/BlurTextField';
import { useFullUserDetails } from '../../utils/hooks';

const hintIconStyle = {
  left: '100%',
  bottom: '47px',
  height: '0',
  display: 'block',
};

const LastOrderInformationCard = ({
  itemInformation,
  displaySalesOrderLineModal,
  displayPurchaseOrderLineModal,
  commonRecord,
  product,
  type,
}: {
  itemInformation: Maybe<IItemInformation>;
  displaySalesOrderLineModal: (filters: Array<IFilterInput>) => void;
  displayPurchaseOrderLineModal: (filters: Array<IFilterInput>) => void;
  commonRecord?: ICustomer | ISupplier;
  product: IProduct;
  type: 'SALES' | 'PURCHASE';
}) => {
  const currencySymbol = useSymbolFromCurrency(BlurTextFieldType.Currency);
  const fullUserDetails = useFullUserDetails();
  const costPermission = fullUserDetails?.companyUser?.costViewPermission;

  const {
    lastPrice,
    lastPriceDate,
    lastPriceUnitDescription,
    lastPriceOrderQuantity,
    lastGrossPrice,
    lastDiscountPercentage,
  } = itemInformation || {};

  const pricePrecision = usePrecision(type === 'SALES' ? 'sales' : 'cost');

  const onLastSalesOrderClick = useCallback(() => {
    displaySalesOrderLineModal([
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'salesOrder.customer.id',
          value: commonRecord?.id,
        },
      },
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: product.id,
        },
      },
    ]);
  }, [displaySalesOrderLineModal, commonRecord?.id, product.id]);

  const onLastPurchaseOrderClick = useCallback(() => {
    displayPurchaseOrderLineModal([
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'purchaseOrder.supplier.id',
          value: commonRecord?.id,
        },
      },
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: product.id,
        },
      },
    ]);
  }, [displayPurchaseOrderLineModal, commonRecord?.id, product.id]);

  if (!lastPriceOrderQuantity || lastPriceOrderQuantity <= 0) {
    return null;
  }

  const discountInformation = getLineDiscountInformation(
    lastGrossPrice,
    lastPrice,
    pricePrecision,
    lastDiscountPercentage
  );

  return (
    <input.ValueCard
      readOnly={true}
      value={
        costPermission === CostViewPermission.All || type === 'SALES'
          ? `${lastPriceOrderQuantity} at ${currencySymbol}${lastPrice?.toFixed(pricePrecision)} 
          ${discountInformation} ${(lastPriceUnitDescription || 'each').toString().toLowerCase()} on ${format(
              new Date(lastPriceDate),
              'do MMM yyyy'
            )}`
          : `${lastPriceOrderQuantity} on ${format(new Date(lastPriceDate), 'do MMM yyyy')}`
      }
      label="Last Order"
      onClick={type === 'SALES' ? onLastSalesOrderClick : onLastPurchaseOrderClick}
      tooltip={`View ${type === 'SALES' ? 'Sales' : 'Purchase'} Order Lines for this ${
        type === 'SALES' ? 'Customer' : 'Supplier'
      }/Product`}
      type={'text'}
    />
  );
};

type IItemInformationProps = {
  image?: Maybe<string>;
  commonRecord: ICustomer | ISupplier;
  product: IProduct;
  rowIndex: number;
  headerEntity: ISalesOrder | IPurchaseOrder | ISalesInvoice | ISalesCredit | ISupplierReturn;
  type: 'SALES' | 'PURCHASE';
  itemInformation: Maybe<IItemInformation>;
  showCostFieldsPurchasing: boolean;
};

const ItemInformation = ({
  image,
  commonRecord,
  product,
  rowIndex,
  headerEntity,
  type,
  itemInformation,
  showCostFieldsPurchasing,
}: IItemInformationProps) => {
  const [productState, setProductState] = useState<{ modalOpen: boolean; id?: number; rowIdx?: number }>({
    modalOpen: false,
  });
  const [salesOrderLineState, setSalesOrderLineState] = useState<{ modalOpen: boolean; filters?: Array<IFilterInput> }>(
    { modalOpen: false }
  );
  const [purchaseOrderLineState, setPurchaseOrderLineState] = useState<{
    modalOpen: boolean;
    filters?: Array<IFilterInput>;
  }>({ modalOpen: false });
  const [returnWorkspaceState, setReturnWorkspaceState] = useState<{
    modalOpen: boolean;
    filters?: Array<IFilterInput>;
  }>({ modalOpen: false });

  if (!itemInformation) return null;

  const {
    orderQuantityMinimum,
    level,
    onOrder,
    outstanding,
    awaitingDispatch,
    leadTimeDays,
    message,
    freeStock,
    makeable,
    orderQuantityIncrement,
    inProduction,
    returned,
    minimumLevel,
    maximumLevel,
    optimumLevel,
    warehouseIsExcludeFromGeneralFreeStock,
    itemUsage,
  } = itemInformation;

  if (message) {
    return <div>{message}</div>;
  }

  const { freeStockAvailability, averageForPeriod, totalUsage } = itemUsage!;

  const displaySalesOrderLineModal = (filters: Array<IFilterInput>) => {
    setSalesOrderLineState({ modalOpen: true, filters: filters });
  };

  const displayPurchaseOrderLineModal = (filters: Array<IFilterInput>) => {
    setPurchaseOrderLineState({ modalOpen: true, filters: filters });
  };

  const displayProductFormModal = () => {
    setProductState({ modalOpen: true, id: product.id, rowIdx: rowIndex });
  };

  const displayReturnWorkspaceModal = (filters: Array<IFilterInput>) => {
    setReturnWorkspaceState({ modalOpen: true, filters: filters });
  };

  return (
    <>
      <ReactTooltip id={'itemInformationTooltip'} />
      <WorkspaceModal
        component={SalesOrderLineWorkspace}
        viewScreenName={'/sales/SalesOrderLineWorkspace/Modal'}
        modalState={salesOrderLineState}
        closeModal={() => {
          setSalesOrderLineState({ modalOpen: false });
        }}
      />
      <WorkspaceModal
        component={PurchaseOrderLineWorkspace}
        viewScreenName={'/Purchasing/OrderLines/Modal/'}
        modalState={purchaseOrderLineState}
        closeModal={() => {
          setPurchaseOrderLineState({ modalOpen: false });
        }}
      />
      <WorkspaceModal
        component={SalesCreditWorkspace}
        viewScreenName={'/Customers/Credit/Modal/'}
        modalState={returnWorkspaceState}
        closeModal={() => {
          setReturnWorkspaceState({ modalOpen: false });
        }}
      />
      <ProductFormModal
        modalState={productState}
        closeModal={() => {
          setProductState({ modalOpen: false });
        }}
        miniForm={false}
        activeTabId={'stockLevel'}
        readOnly={true}
      />
      <Form.Row>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard
            readOnly={true}
            value={level || 0}
            label="On Hand"
            onClick={displayProductFormModal}
            tooltip={'View Product Stock Levels'}
            decimals={product?.precision}
          />
          <input.ValueCard
            readOnly={true}
            value={freeStock || 0}
            label="Free Stock"
            showTag={true}
            decimals={product?.precision}
            tooltip={
              warehouseIsExcludeFromGeneralFreeStock ? 'Selected Warehouse is Excluded From General Free Stock' : null
            }
          >
            {warehouseIsExcludeFromGeneralFreeStock ? (
              <HintIcon style={hintIconStyle} hint={'Excluded From General Free Stock'} />
            ) : null}
          </input.ValueCard>
          <input.ValueCard
            readOnly={true}
            value={freeStockAvailability || 0}
            decimals={product?.precision}
            label="Availability (Months)"
          />
          {returned > 0 ? (
            <input.ValueCard
              readOnly={true}
              value={returned || 0}
              label="Returned"
              decimals={product?.precision}
              onClick={() => {
                displayReturnWorkspaceModal([
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'salesOrder.id',
                      value: headerEntity?.id,
                    },
                  },
                ]);
              }}
            />
          ) : null}
          {!!leadTimeDays ? <input.ValueCard readOnly={true} value={leadTimeDays} label="Lead Time (Days)" /> : null}
        </Form.Fieldset>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard readOnly={true} value={outstanding || 0} decimals={product?.precision} label="Outstanding" />
          <input.ValueCard
            readOnly={true}
            value={awaitingDispatch || 0}
            decimals={product?.precision}
            label="Awaiting Dispatch"
            onClick={() =>
              displaySalesOrderLineModal([
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'product.id',
                    value: product.id,
                  },
                },
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'salesOrderLineStatus',
                    value: 'AWAITING_PRODUCTION',
                  },
                },
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'salesOrderLineStatus',
                    value: 'AWAITING_ALLOCATION',
                  },
                },
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'salesOrderLineStatus',
                    value: 'ASSIGNED',
                  },
                },
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'salesOrderLineStatus',
                    value: 'ALLOCATED',
                  },
                },
              ])
            }
            tooltip={'View Assigned/Allocated/Awaiting Allocation Lines for Product'}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard
            readOnly={true}
            value={freeStockAvailability || 0}
            decimals={product.precision}
            label="Availability (Months)"
          />
          <input.ValueCard
            readOnly={true}
            value={averageForPeriod || 0}
            decimals={product.precision}
            label="Average Usage per Month"
          />
          <input.ValueCard readOnly={true} value={totalUsage ?? 0} decimals={product.precision} label="Total Usage" />
        </Form.Fieldset>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard
            readOnly={true}
            value={makeable < maxMakeable ? makeable || 0 : '∞'}
            label="Makeable"
            decimals={product.precision}
          />
          <input.ValueCard
            readOnly={true}
            value={inProduction || 0}
            label="In production"
            decimals={product.precision}
          />
          {returned > 0 ? (
            <input.ValueCard
              readOnly={true}
              value={returned || 0}
              label="Returned"
              decimals={product.precision}
              onClick={() => {
                displayReturnWorkspaceModal([
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'salesOrder.id',
                      value: headerEntity.id,
                    },
                  },
                ]);
              }}
            />
          ) : null}
        </Form.Fieldset>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard
            readOnly={true}
            value={onOrder || 0}
            label="On Order"
            decimals={product.precision}
            onClick={() =>
              displayPurchaseOrderLineModal([
                {
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: 'product.id',
                    value: product.id,
                  },
                },
                {
                  filterOperation: FilterOperation.NotEquals,
                  filterParameters: {
                    field: 'purchaseOrder.archived',
                    value: 'true',
                  },
                },
              ])
            }
            tooltip={'View Purchase Order Lines for Product'}
          />
          <input.ValueCard
            readOnly={true}
            value={orderQuantityIncrement || 0}
            decimals={product.precision}
            label="Order Increment"
          />
          {!!leadTimeDays ? <input.ValueCard readOnly={true} value={leadTimeDays} label="Lead Time (Days)" /> : null}
        </Form.Fieldset>
        <Form.Fieldset cols={0.12}>
          <input.ValueCard
            readOnly={true}
            value={minimumLevel || 0}
            label="Minimum Level"
            decimals={product.precision}
          />
          <input.ValueCard
            readOnly={true}
            value={maximumLevel || 0}
            label="Maximum Level"
            decimals={product.precision}
          />
          <input.ValueCard
            readOnly={true}
            value={optimumLevel || 0}
            label="Optimum Level"
            decimals={product.precision}
          />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 5}>
          <input.ValueCard
            readOnly={true}
            value={orderQuantityMinimum || 0}
            label="Minimum Order"
            decimals={product.precision}
          />
          {showCostFieldsPurchasing ? (
            <LastOrderInformationCard
              itemInformation={itemInformation}
              displaySalesOrderLineModal={displaySalesOrderLineModal}
              displayPurchaseOrderLineModal={displayPurchaseOrderLineModal}
              commonRecord={commonRecord}
              product={product}
              type={type}
            />
          ) : null}
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 6}>
          <div style={{ height: '150px' }}>
            <img src={`/api/resources/image/${image}`} style={{ maxHeight: '100%', marginLeft: '50px' }} />
          </div>
        </Form.Fieldset>
      </Form.Row>
    </>
  );
};

export default ItemInformation;
